import { Helmet } from "react-helmet-async";

export interface SEOHeadProps {
  title: string;
  description: string;
  path: string; // e.g. "/", "/blog"
  image?: string;
  type?: "website" | "article" | "profile";
  keywords?: string;
  noindex?: boolean;
  publishedTime?: string;
  modifiedTime?: string;
  tags?: string[];
  /** Additional JSON-LD blocks. Each becomes its own <script type="application/ld+json">. */
  jsonLd?: Record<string, unknown>[];
}

const SITE_URL = "https://mustakim.iam.bd";
const SITE_NAME = "Mustakim Nur Salin";
const DEFAULT_IMAGE = "https://mustakim.iam.bd/og-cover.png";

/**
 * Per-route head metadata. Replaces sitewide title/description/canonical/og:*
 * from index.html for JS-executing crawlers (Googlebot). Static crawlers
 * (Slack/Facebook) fall back to the index.html defaults.
 */
const SEOHead = ({
  title,
  description,
  path,
  image = DEFAULT_IMAGE,
  type = "website",
  keywords,
  noindex,
  publishedTime,
  modifiedTime,
  tags,
  jsonLd = [],
}: SEOHeadProps) => {
  const url = `${SITE_URL}${path}`;
  const fullTitle = path === "/" ? title : `${title} | ${SITE_NAME}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta
        name="robots"
        content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"}
      />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />
      <meta property="og:locale" content="en_US" />
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      {tags?.map((t) => (
        <meta key={t} property="article:tag" content={t} />
      ))}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={title} />
      <meta name="twitter:creator" content="@mustakim_error" />
      <meta name="twitter:site" content="@mustakim_error" />

      {jsonLd.map((data, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
};

export const SITE_META = { SITE_URL, SITE_NAME, DEFAULT_IMAGE };

export default SEOHead;
